import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { TranslateService } from '@ngx-translate/core';

import { QueryClient } from '@tanstack/angular-query-experimental';

import { ToastService } from '@services/toast.service';
import { FeedPostsService } from '../services/feed-posts.service';
import { FeedApiService } from 'src/app/api/queries/feed-api.service';
import { FeedFiltersService } from '../services/feed-filters.service';
import { FilterItemResolveResponse } from 'src/app/api/queries/feed-api.service';

import { BusinessConstraintsInfoDialogComponent } from '../components/popups/business-constraints-info-dialog/business-constraints-info-dialog.component';

import { feedPostsQueryKey } from 'src/app/api/queries/feed.queries';
import { allWidgetDataQueriesFilter } from 'src/app/api/queries/widget.queries';

import { DialogService } from '@sui/dialog';

import { NomObjectTypes } from '../constants';
import { FilterItem } from '../models/core.models';

import {
  periodChanged,
  applyDefaultPeriod,
  selectPreviousPeriod,
  togglePeriodLockedSuccess,
} from '../actions/core.actions';
import * as feedActions from '../actions/feed.actions';

import {
  selectUser,
  selectFeed,
  selectFeedFilters,
  getContentFilters,
  selectCurrentObject,
} from '../reducers';
import { AnyFilterItem, findFilter } from '../reducers/feed.reducer';

import { EMPTY, Observable, of } from 'rxjs';
import {
  tap,
  map,
  filter,
  switchMap,
  catchError,
  withLatestFrom,
} from 'rxjs/operators';

@Injectable()
export class FeedEffects {
  private readonly queryClient = inject(QueryClient);
  private readonly feedApi = inject(FeedApiService);
  private readonly feedPosts = inject(FeedPostsService);
  private readonly feedFilters = inject(FeedFiltersService);

  /**
   * A **client-state** change that alters what the feed shows: refresh the
   * widgets, re-read every expanded filter, and reload the posts list when the
   * cue did not already do so by moving its query key. Legitimate NgRx → Query
   * glue: the filters and period live in reducers, so no write owns these
   * moments. (Writes refresh through `injectFeedWriteRefresh` in their owners.)
   */
  filtersChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          periodChanged,
          applyDefaultPeriod,
          selectPreviousPeriod,
          feedActions.activateFilters,
          feedActions.clearFilters,
          feedActions.deactivateFilterItem,
          feedActions.updateActiveFilterItem,
          feedActions.searchPosts,
          feedActions.showSelectedPostsOnly,
        ),
        withLatestFrom(
          this.store.select(selectCurrentObject),
          this.store.select(selectFeed),
          this.store.select(selectFeedFilters),
          this.store.select(getContentFilters),
        ),
        switchMap(([action, object, feed, filters, contentFilters]) => {
          // ! Posts: a cue that changes `getContentFilters` moves the list
          // ! query's key, which refetches on its own. But some cues leave the
          // ! key unchanged - `activateFilters` re-dispatched as a refresh
          // ! signal (breadcrumb chip / Refresh item, dashboard auto-refresh,
          // ! drilldown on an already-active value) - and those can only reload
          // ! via invalidation. The post-cue key already sitting in the cache
          // ! is the tell: an unchanged key is always cached, a new key fetches
          // ! by itself.
          if (feed) {
            const postsKey = feedPostsQueryKey({
              feedId: feed.id,
              contentFilters,
            });

            if (this.queryClient.getQueryState(postsKey)) {
              this.feedPosts.invalidate();
            }
          }

          // ! No edit-mode guard on purpose. These actions are also the
          // ! Dashboard's "reload the widgets" signal (auto-refresh, the toolbar
          // ! Refresh button, the breadcrumb chips), and gating them on the feed's
          // ! compact-view flag silently killed all of it on dashboards. Compact
          // ! view needs no guard anyway: it renders `widget-card-list`, so no
          // ! widget-data query is mounted for this to invalidate.
          this.queryClient.invalidateQueries(allWidgetDataQueriesFilter());

          // ! Every already-expanded filter is re-read from offset 0: the item
          // ! counts are narrowed by the content filters that just changed. A
          // ! filter still at `offset === -1` was never expanded.
          filters.forEach((filter) => {
            if (filter.offset !== -1) {
              this.feedFilters.loadFilterItems(
                object.id,
                filter,
                contentFilters,
                0,
              );
            }
          });

          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  // ! Dark mode's counterpart invalidation lives in
  // ! `UserPreferencesService.toggleDarkMode`, after the remote persist.
  $widgetsViewHasChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.changeWidgetsLanaguage),
        withLatestFrom(this.store.select(selectUser)),
        switchMap(([action, user]) => {
          if (!user) {
            return EMPTY;
          }

          this.queryClient.invalidateQueries(allWidgetDataQueriesFilter());

          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  feedCreateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.createFeedSuccess),
        tap((action) => {
          const messageKey =
            action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION
              ? 'feed.toasts.feed-created-successfully'
              : 'feed.toasts.dimension-created-successfully';
          this.toastService.showSuccess(
            this.translateService.instant(messageKey),
          );

          // A new feed opens itself; a new search dimension has no page of its
          // own, so land on the workspace.
          if (action.feed.object_type_id != NomObjectTypes.SEARCH_DIMENSION) {
            this.router.navigate([
              'analytics',
              'workspace',
              action.feed.workspace_id,
              'feed',
              action.feed.id,
            ]);
          } else {
            this.router.navigate([
              'analytics',
              'workspace',
              action.feed.workspace_id,
            ]);
          }
        }),
      ),
    { dispatch: false },
  );

  periodChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(periodChanged),
        tap((action) => {
          this.updateQueryParamsSilently({
            start_time: action.period.startTimestamp,
            end_time: action.period.endTimestamp,
          });
        }),
      ),
    { dispatch: false },
  );

  defaultPeriodChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(applyDefaultPeriod),
        tap((action) => {
          this.updateQueryParamsSilently({
            start_time: null,
            end_time: null,
          });
        }),
      ),
    { dispatch: false },
  );

  togglePeriodLockedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(togglePeriodLockedSuccess),
        tap(({ isPeriodLocked }) => {
          if (isPeriodLocked) {
            this.updateQueryParamsSilently({
              start_time: null,
              end_time: null,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  pageChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.pageChanged),
        tap((action) => {
          this.updateQueryParamsSilently({
            page: action.pageNumber,
          });
        }),
      ),
    { dispatch: false },
  );

  duplicateFeedsConstraintError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(feedActions.duplicateFeedsConstraintError),
        tap((action) => {
          this.dialogService.openSimpleDialog(
            BusinessConstraintsInfoDialogComponent,
            {
              data: {
                errors: action.errors,
              },
              closeOnClick: false,
            },
          );
        }),
      ),
    { dispatch: false },
  );

  /** Merge params into the current URL without adding a history entry. */
  updateQueryParamsSilently(params: { [key: string]: any }) {
    const currentRoute = this.router.routerState.snapshot.root;

    let currentQueryParams = {};
    if (currentRoute) {
      currentQueryParams = currentRoute.queryParams;
    }

    const mergedQueryParams = { ...currentQueryParams, ...params };

    const url = this.router
      .createUrlTree([], { queryParams: mergedQueryParams })
      .toString();

    this.location.replaceState(url);
  }

  toggleFilterDimension$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.toggleFilterDimension),
      filter((action) => action.activate === true),
      map(() => feedActions.activateFilters()),
    ),
  );

  selectFilterItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(feedActions.selectFilterItem),
      concatLatestFrom(() => this.store.select(selectFeedFilters)),
      switchMap(([{ filterItem, mode }, filters]) => {
        const existingFilter = findFilter(filters, filterItem);

        // Found in the loaded filters: reuse its meta
        if (existingFilter) {
          const resolvedItem: AnyFilterItem = {
            ...filterItem,
            type: mode,
            filter_title: existingFilter.name,
          };

          return of(
            feedActions.selectFilterItemResolved({ item: resolvedItem }),
          );
        }

        // Not loaded: resolve the titles from the backend.
        // ! An effect on purpose, not a hook: nothing holds the response, it only
        // ! enriches one action with titles, and most of its dispatch sites are
        // ! `widget.effects`' point-click cluster, which builds `Action[]` arrays.
        const request$: Observable<FilterItemResolveResponse> =
          this.feedApi.getValueFilterItemMeta(
            filterItem.property_uri,
            (filterItem as FilterItem).raw_value,
          );

        return request$.pipe(
          switchMap((response) => {
            const resolvedItem: AnyFilterItem = {
              ...filterItem,
              type: mode,
              title: response.property_value_title ?? filterItem.title,
              filter_title: response.property_title ?? filterItem.filter_title,
            };

            return of(
              feedActions.selectFilterItemResolved({ item: resolvedItem }),
            );
          }),
          catchError((error) =>
            of(feedActions.selectFilterItemResolveFailed({ error })),
          ),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private toastService: ToastService,
    private router: Router,
    private location: Location,
    private translateService: TranslateService,
    private dialogService: DialogService,
  ) {}
}
